import { Component, OnInit } from '@angular/core';
import { FormGroup, FormBuilder, Validators, FormArray, ValidatorFn, AbstractControl, ValidationErrors } from '@angular/forms';
import { BanksService } from '../../../services/banks.service';
import { ThirdPartiesService } from '../../../services/third-parties.service';
import { WorkOrdersService } from '../../../services/work-orders.service';
import { ToastService } from '../../../services/toast.service';
import { ExpenseVouchersService } from '../../../services/expense-vouchers.service';
import { forkJoin } from 'rxjs';
import { LoadingService } from '../../../services/loading.service';
import { Router } from '@angular/router';
import { FeeTypesService } from "../../../services/fee-types.service";
import { BaseFormComponent } from '../../shared/base-form/base-form.component';
import { ConfirmationService } from '../../../services/confirmation.service';
import { ConfirmationModalComponent } from '../../shared/confirmation-modal/confirmation-modal.component';

@Component({
  selector: 'app-new-expense-receipt',
  templateUrl: './new-expense-receipt.component.html',
  styleUrl: './new-expense-receipt.component.scss'
})
export class NewExpenseReceiptComponent extends BaseFormComponent implements OnInit {

  expenseVoucherForm!: FormGroup;
  banks: any = []; // Aquí cargarías los bancos disponibles
  thirdParties: any = []; // Aquí cargarías los terceros disponibles
  public workOrders: any = [];
  feeTypes: any = [];

  // Summary properties
  showSummaryModal: boolean = false;
  summarySubtotal: number = 0;
  summaryTotalDiscounts: number = 0;
  summaryNetTotal: number = 0;
  summaryDiscountsList: any[] = [];


  constructor(
    private fb: FormBuilder,
    router: Router,
    private _banksService: BanksService,
    private _thirdPartiesService: ThirdPartiesService,
    private _workOrdersService: WorkOrdersService,
    private toastService: ToastService,
    private _expenseVoucherService: ExpenseVouchersService,
    private _loadingService: LoadingService,
    private _feeTypesService: FeeTypesService,
    confirmationService: ConfirmationService
  ) {
    super(confirmationService, router);
  }

  // === MÉTODOS REQUERIDOS POR BaseFormComponent ===

  protected override initializeForm(): void {
    // El formulario se inicializa en ngOnInit
  }

  protected override getMainForm(): FormGroup {
    return this.expenseVoucherForm;
  }

  public override getFormName(): string {
    return 'comprobante de egreso';
  }

  public override getCriticalData(): string[] {
    const data: string[] = [];

    // Información del tercero
    const thirdPartyId = this.expenseVoucherForm.get('thirdPartyId')?.value;
    const thirdParty = this.thirdParties?.find((t: any) => t.id === thirdPartyId);
    if (thirdParty) {
      data.push(`Tercero: ${thirdParty.name}`);
    }

    // Información del banco
    const bankId = this.expenseVoucherForm.get('bankId')?.value;
    const bank = this.banks?.find((b: any) => b.id === bankId);
    if (bank) {
      data.push(`Banco: ${bank.bankName}`);
    }

    // Conceptos de gasto
    const items = this.expenseVoucherItems;
    if (items.length > 0) {
      data.push(`Elementos: ${items.length}`);
      const totalAmount = items.controls.reduce((total, control) => {
        return total + (parseFloat(control.get('amount')?.value) || 0);
      }, 0);
      data.push(`Total: ${totalAmount.toLocaleString('es-CO', { style: 'currency', currency: 'COP' })}`);
    }

    return data;
  }

  protected override async performFormSubmit(): Promise<void> {
    await this.submitExpenseVoucher();
  }

  protected override navigateBack(): void {
    this.router.navigate(['/expense-receipts']);
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this.loadInitialData();
    this.expenseVoucherForm = this.fb.group({
      bankId: ['', Validators.required],
      thirdPartiesId: ['', Validators.required],
      concept: ['', Validators.required],
      dateExpenseVoucher: ['', Validators.required],
      totalAmount: [0, Validators.required],
      expenseVouchersItems: this.fb.array([], this.atLeastOneValidItem()),
      feeDetails: this.fb.array([])
    });
  }

  loadInitialData() {
    this._loadingService.show();
    forkJoin({
      thirdParties: this._thirdPartiesService.getAll(),
      banks: this._banksService.getAll(),
      feeTypes: this._feeTypesService.getAll()
    }).subscribe({
      next: (results) => {
        // Manejar estructura paginada: data puede ser array directo o {data: [...], totalCount: ...}
        const thirdPartiesData = Array.isArray(results.thirdParties.data) ? results.thirdParties.data : (results.thirdParties.data?.data || []);
        const banksData = Array.isArray(results.banks.data) ? results.banks.data : (results.banks.data?.data || []);
        // feeTypes siempre devuelve un array directamente en data
        const feeTypesData = results.feeTypes.data || [];

        this.thirdParties = thirdPartiesData.map((data: any) => ({ label: `Documento: ${data.documentId} Nombres: ${data.firstName} ${data.lastName}`, value: data.id, data }));
        this.banks = banksData.map((data: any) => ({ label: `${data.bankName} ${data.accountNumber}`, value: data.id, data }));
        this.feeTypes = feeTypesData;
        this._loadingService.hide();
      },
      error: (err) => {
        console.error('Error loading initial data', err);
        this._loadingService.hide();
      }
    });
  }

  // 🔍 Búsqueda server-side para terceros
  onSearchThirdParties(event: any): void {
    const query = event.filter;
    if (!query || query.length < 3) {
      // Recargar datos iniciales
      this._thirdPartiesService.getAll().subscribe({
        next: (results) => {
          const data = Array.isArray(results.data) ? results.data : (results.data?.data || []);
          this.thirdParties = data.map((d: any) => ({
            label: `Documento: ${d.documentId} Nombres: ${d.firstName} ${d.lastName}`,
            value: d.id,
            data: d
          }));
        },
        error: (err) => console.error('Error loading third parties:', err)
      });
      return;
    }

    this._thirdPartiesService.getAllFiltered(query).subscribe({
      next: (results) => {
        const data = Array.isArray(results.data) ? results.data : (results.data?.data || []);
        this.thirdParties = data.map((d: any) => ({
          label: `Documento: ${d.documentId} Nombres: ${d.firstName} ${d.lastName}`,
          value: d.id,
          data: d
        }));
      },
      error: (err) => console.error('Error searching third parties:', err)
    });
  }


  getAllWorkOrders(id?: number, status?: number) {
    this._workOrdersService.getAll(id, status).subscribe({
      next: response => {
        this.workOrders = response.data;
      },
      error: e => {
        console.log(e)
      }
    })
  }

  get expenseVoucherItems(): FormArray {
    return this.expenseVoucherForm.get('expenseVouchersItems') as FormArray;
  }

  atLeastOneValidItem(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const formArray = control as FormArray;
      const isValid = formArray.controls.some(item => item.valid);

      return isValid ? null : { atLeastOneItemRequired: true };
    };
  }

  addExpenseVoucherItem() {

    const thirdPartySelected = this.expenseVoucherForm.get('thirdPartiesId')?.value;

    if (!thirdPartySelected) {
      this.handleError("Debe seleccionar un tercero primero.");
      // Opcional: puedes mostrar un mensaje de error al usuario
      this.expenseVoucherForm.get('thirdPartiesId')?.markAsTouched();
      this.expenseVoucherForm.get('thirdPartiesId')?.setErrors({ required: true });
      return;
    }

    const item = this.fb.group({
      workOrdersId: ['', Validators.required],
      date: [''],
      details: [''],
      payer: [''],
      totalBaseAmount: [0],
      totalVatAmount: [0],
      totalToPay: [0],
    });

    // Suscribir al campo amount para actualizar totalAmount
    item.get('totalToPay')?.valueChanges.subscribe(() => {
      this.updateTotalAmount();
    });
    this.expenseVoucherItems.push(item);
    this.updateTotalAmount();
  }

  // Eliminar un ítem de orden de trabajo
  removeExpenseVoucherItem(index: number): void {
    if (this.expenseVoucherItems.length > 1) {
      this.expenseVoucherItems.removeAt(index);
      this.updateTotalAmount();
    }
  }

  handleGetWorkOrder(data: any) {
    const { value } = data
    this.getAllWorkOrders(Number(value), 1);
  }

  handleGetDetailsForWorkOrder(event: any, index: number) {
    const id = event.target.value;

    if (this.isWorkOrderAlreadySelected(id, index)) {
      this.handleError('Esta orden de trabajo ya fue seleccionada en otro ítem.');
      const itemGroup = (this.expenseVoucherForm.get('expenseVouchersItems') as FormArray).at(index) as FormGroup;
      itemGroup.get('workOrdersId')?.setValue('');
      return;
    }

    const result = this.workOrders.find((x: any) => x.id == id);
    if (result != null) {
      const itemGroup = (this.expenseVoucherForm.get('expenseVouchersItems') as FormArray).at(index) as FormGroup;
      itemGroup.get('date')?.setValue(result.paymentDate);
      const payerName = result.payer
        ? `${result.payer.firstName} ${result.payer.lastName}`
        : 'Sin ordenante';
      itemGroup.get('payer')?.setValue(payerName);
      itemGroup.get('totalBaseAmount')?.setValue(result.totalBaseAmount);
      itemGroup.get('totalVatAmount')?.setValue(result.totalVatAmount);
      itemGroup.get('totalToPay')?.setValue(result.totalToPay);
    }
  }

  private isWorkOrderAlreadySelected(workOrderId: string, currentIndex: number): boolean {
    return this.expenseVoucherItems.controls.some((control, i) =>
      i !== currentIndex && control.get('workOrdersId')?.value == workOrderId
    );
  }


  getWorkOrderUrl(workOrdersId: string): string {
    return `/work-orders/view/${workOrdersId}`;
  }

  trackAmountChanges(): void {
    this.expenseVoucherItems.valueChanges.subscribe(() => {
      this.updateTotalAmount();
    });
  }

  updateTotalAmount(): void {
    console.log("entrando update total amount")
    const total = this.expenseVoucherItems.controls.reduce((acc, control) => {
      const amount = control.get('totalToPay')?.value || 0;
      return acc + parseFloat(amount);
    }, 0);
    this.expenseVoucherForm.get('totalAmount')?.setValue(total, { emitEvent: false });
  }


  // Fee Actions
  get feeDetails(): FormArray {
    return this.expenseVoucherForm?.get('feeDetails') as FormArray || this.fb.array([]);
  }

  addFeeDetail() {
    const feeGroup = this.fb.group({
      feeTypeId: ['', Validators.required],
      requestAmount: ['', [Validators.required, Validators.min(1)]],
      description: ['']
    });

    this.feeDetails.push(feeGroup);
  }

  // Eliminar un Fee
  removeFeeDetail(index: number) {
    this.feeDetails.removeAt(index);
  }

  async onSubmit(): Promise<void> {
    if (this.expenseVoucherForm.invalid) {
      this.expenseVoucherForm.markAllAsTouched();
      return;
    }
    this.calculateSummary();
    this.showSummaryModal = true;
  }

  calculateSummary(): void {
    // 1. Subtotal: Sum of items (already stored in totalAmount or recalculated)
    // The current totalAmount logic sums 'totalToPay' of items. 
    // Assuming 'totalAmount' in form is the base Subtotal before global discounts (feeDetails).
    // Let's verify: default logic updates 'totalAmount' based on items. This seems to be the subtotal for the expense.

    // Recalculate subtotal from items to be safe
    this.summarySubtotal = this.expenseVoucherItems.controls.reduce((acc, control) => {
      const amount = control.get('totalToPay')?.value || 0;
      return acc + parseFloat(amount);
    }, 0);

    // 2. Calculate Discounts
    this.summaryTotalDiscounts = 0;
    this.summaryDiscountsList = [];

    const feeControls = this.feeDetails.controls;
    feeControls.forEach(control => {
      const typeId = control.get('feeTypeId')?.value;
      const typeObj = this.feeTypes.find((f: any) => f.id == typeId);
      const typeName = typeObj ? typeObj.name : 'Descuento';

      const reqAmountStr = control.get('requestAmount')?.value?.toString() || '0';
      const description = control.get('description')?.value || '';

      let discountValue = 0;

      // Check if percentage
      if (reqAmountStr.includes('%')) {
        const percentage = parseFloat(reqAmountStr.replace('%', ''));
        if (!isNaN(percentage)) {
          discountValue = (this.summarySubtotal * percentage) / 100;
        }
      } else {
        discountValue = parseFloat(reqAmountStr);
      }

      if (isNaN(discountValue)) discountValue = 0;

      this.summaryTotalDiscounts += discountValue;
      this.summaryDiscountsList.push({
        name: typeName,
        description: description,
        value: discountValue,
        originalInput: reqAmountStr
      });
    });

    // 3. Net Total
    this.summaryNetTotal = this.summarySubtotal - this.summaryTotalDiscounts;
  }

  getSelectedBankLabel(): string {
    const bankId = this.expenseVoucherForm.get('bankId')?.value;
    if (!bankId || !this.banks) return 'Sin banco';
    const bank = this.banks.find((b: any) => b.value === bankId);
    return bank?.label || 'Sin banco';
  }

  getSelectedThirdPartyLabel(): string {
    const thirdPartyId = this.expenseVoucherForm.get('thirdPartiesId')?.value;
    if (!thirdPartyId || !this.thirdParties) return 'Sin tercero';
    const tp = this.thirdParties.find((t: any) => t.value === thirdPartyId);
    return tp?.label || 'Sin tercero';
  }

  getExpenseConcept(): string {
    return this.expenseVoucherForm.get('concept')?.value || 'Sin concepto';
  }

  getFormattedExpenseDate(): string {
    const date = this.expenseVoucherForm.get('dateExpenseVoucher')?.value;
    if (!date) return 'Sin fecha';
    const d = new Date(date);
    return d.toLocaleDateString('es-CO', { day: '2-digit', month: '2-digit', year: 'numeric' });
  }

  cancelSummary(): void {
    this.showSummaryModal = false;
  }

  async confirmSave(): Promise<void> {
    await this.submitExpenseVoucher();
    this.showSummaryModal = false; // Close after save
  }

  private async submitExpenseVoucher(): Promise<void> {
    this._loadingService.show();
    console.log("before", this.expenseVoucherForm.value)
    const newExpenseVoucher = {
      ...this.expenseVoucherForm.value,
      bankId: Number(this.expenseVoucherForm.value.bankId),
      thirdPartiesId: Number(this.expenseVoucherForm.value.thirdPartiesId),
      expenseVouchersItems: this.expenseVoucherForm.value.expenseVouchersItems.map((data: any, index: number) => ({
        workOrdersId: Number((this.expenseVoucherItems.at(index) as FormGroup).get('workOrdersId')?.value),
        amountPaid: Number((this.expenseVoucherItems.at(index) as FormGroup).get('totalToPay')?.value)
      }))
    };
    this._expenseVoucherService.insert(newExpenseVoucher).subscribe({
      next: (response: any) => {
        this._loadingService.hide();
        console.error("mostrando response", response)
        this.handleSuccess('Comprobante de egreso creado exitosamente');
      },
      error: (err) => {
        this._loadingService.hide();
        console.error("se presentó un error", err)
        this.handleError('Error al crear el comprobante de egreso');
      }
    })
    console.log("after", newExpenseVoucher)
  }

  private handleSuccess(message: string): void {
    this._loadingService.hide();
    this.toastService.showToast('success', message);
    this.router.navigate(['/expense-receipts']);
  }

  private handleError(message: string): void {
    this._loadingService.hide();
    this.toastService.showToast('error', message);
  }

  // === MÉTODOS DE CANCELACIÓN Y NAVEGACIÓN ===

  /**
   * Maneja el botón cancelar con confirmación
   */
  async onCancel(): Promise<void> {
    await this.handleFormCancel('/expense-receipts');
  }

  /**
   * Confirmación para gastos elevados
   */
  async confirmLargeExpense(amount: number): Promise<boolean> {
    return await this.confirmationService.expenseReceipt.largeExpense(amount);
  }

  /**
   * Maneja la confirmación del modal
   */
  onConfirmationModalConfirmed(): void {
    this.onModalConfirmed();
  }

  /**
   * Maneja la cancelación del modal
   */
  onConfirmationModalCancelled(): void {
    this.onModalCancelled();
  }

  /**
   * Maneja el cierre del modal
   */
  onConfirmationModalClosed(): void {
    this.onModalClosed();
  }
}
